<template>
  <div class="min-h-screen bg-primary w-full h-full mt-[60px] lg:mt-0 themeTransition">
    <Transition
      enter-active-class="duration-1000 page-enter-transition"
      leave-active-class="duration-300 page-leave-transition"
      enter-from-class="page-enter-from"
      leave-to-class="page-enter-from"
    >
      <div v-if="!isLoading">
        <div class="slider fixed h-screen w-screen bg-orange z-10" />
        <page-wrapper v-if="pageGUI" :page-gui="pageGUI">
          <!-- content begin -->
          <page-hero v-bind="{ ...pageHero }" />
          <div id="content" class="col-span-full" v-if="sections && sections.length > 0">
            <component
              v-for="(section, index) in sections"
              :key="index"
              :is="getComponent(section?.name as ComponentType)"
              :ref="section?.name"
              v-bind="{ ...section?.props }"
            ></component>
          </div>
          <social-module
            :url="currentUrl ?? ''"
            :label="shareLabel.valueOf()"
            :icons="[...iconArray]"
          />
          <expert-module v-if="expertData?.expert" v-bind="{ ...expertData }" />
          <news-card-section
            v-if="relatedInsightsSection"
            :theme="theme"
            :title="relatedInsightsSection?.title"
            :cards="relatedInsightsSection?.cards"
            :cta-label="relatedInsightsSection?.ctaLabel"
            :cta-link="relatedInsightsSection?.ctaLink"
            :target="relatedInsightsSection?.target"
            :isRelated="true"
            classes="pb-24 md:pb-32"
          />
          <!-- content end -->
        </page-wrapper>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, computed } from "vue";
import { parseUrl, changeTheme } from "@/util/helper";

import PageWrapper from "@components/PageWrapper.vue";
import PageHero, { HeroSectionProps } from "@organisms/PageHero/PageHero.ce.vue";
import NewsCardSection from "@organisms/NewsCardSection/NewsCardSection.ce.vue";
import SocialModule from "@molecules/Socials/Socials.ce.vue";
import ExpertModule from "@molecules/ExpertModule/ExpertModule.ce.vue";

import { IRelatedLinkProps, Theme } from "@/types/types";
import {
  getPage,
  getPageByID,
  getRelatedInsights,
  getRelatedItemsLink,
  getTranslations,
} from "@/util/api";
import { IInsightPage } from "@/types/IInsightPage";
import { getComponentData, getComponent } from "@/util/getComponent";
import { ComponentType, RelatedItemsType } from "@/util/constants";
import { IExpertModuleProps } from "@/types/components/IExpertModuleProps";

const isLoading = ref(true);
const environment = ref();
const urlParams = ref<String[] | null>(null);

const pageData = ref<IInsightPage | null>(null);
const pageGUI = ref<string | undefined>();

const theme = ref<Theme | null>(null);

const relatedInsights = ref<IInsightPage[] | null>(null);
const relatedInsightsLink = ref<IRelatedLinkProps | null>(null);

const iconArray = ref<Array<{ icon: string }>>([]);
const currentUrl = ref<string | null>(null);
const shareLabel = ref<string>("");

const isPreview = ref<boolean>(false);
const previewUrl = ref<string>("");
const pageKey = ref<string | undefined>();

onBeforeMount(async () => {
  pageKey.value = document.documentElement.dataset.id;
  //get the meta property og:Url from the head tag
  const metaUrl = document.head.querySelector("meta[property='og:url']");
  if (metaUrl instanceof HTMLMetaElement) {
    previewUrl.value = metaUrl.content.replace(/^https?:\/\/[^/]+/, "");
  }
  environment.value = parseUrl(window.location.href).environment;
  urlParams.value = parseUrl(window.location.href).params;

  //create social share icons
  currentUrl.value = window.location.href;
  iconArray.value = [
    { icon: "Facebook" },
    { icon: "Xicon" },
    { icon: "LinkedIn" },
    { icon: "MailTo" },
  ];
  shareLabel.value = await getTranslations("ShareTitle");

  if (!urlParams.value) return;

  let path = urlParams.value.join("/");
  if (previewUrl.value.indexOf(path) < 0) {
    path = previewUrl.value;
    isPreview.value = true;
  }
  if (path) {
    pageData.value = (await getPageByID(pageKey.value, isPreview.value)) as IInsightPage;
    //pageData.value = (await getPage(path, isPreview.value)) as IInsightPage;
    relatedInsightsLink.value = (await getRelatedItemsLink(
      RelatedItemsType.INSIGHT
    )) as IRelatedLinkProps;
    if (pageData.value) {
      theme.value = pageData.value.properties.backgroundColor;
      if (theme.value) changeTheme(theme.value);
      pageGUI.value = pageData.value.route.startItem.id;
      const sectorGUID = pageData.value.properties.projectSector[0].id;
      relatedInsights.value = await getRelatedInsights(sectorGUID, pageData.value.id);
      isLoading.value = false;
    }
  }
});

const pageProperties = computed(() => {
  return pageData.value?.properties;
});

const pageHero = computed(() => {
  if (!pageProperties.value) return;
  return {
    title: pageProperties.value.title,
    subHeading: pageProperties.value.tag,
    description: pageProperties.value.description
      ? pageProperties.value.description.markup
      : undefined,
    imageAltText: pageProperties.value.imageAltText,
    date: pageProperties.value.date,
    author: pageProperties.value.author || undefined,
    theme: theme.value,
    image: pageProperties.value.image[0],
    tag: pageProperties.value.projectSector[0].name,
  } as HeroSectionProps;
});

const expertData = computed(() => {
  if (!pageProperties.value) return;
  const { expertBackgroundColor, expert, expertTag } = pageProperties.value;
  if (!expert) return;
  return {
    theme: expertBackgroundColor.label,
    expert: expert,
    subtitle: expertTag,
  } as IExpertModuleProps;
});

const relatedInsightsSection = computed(() => {
  if (
    !relatedInsights.value ||
    relatedInsights.value.length === 0 ||
    !relatedInsightsLink.value
  )
    return;

  const insights = relatedInsights.value.map((insight) => {
    const { properties } = insight;
    return {
      title: properties.title,
      image: JSON.stringify(properties.image[0]),
      imgAlt: properties.imageAltText,
      url: insight.route.path,
      tag: properties.projectSector[0].name,
    };
  });
  return {
    theme: theme.value,
    title: relatedInsightsLink.value.pageText,
    cards: JSON.stringify(insights),
    ctaLabel: relatedInsightsLink.value.seeAllName,
    ctaLink: relatedInsightsLink.value.seeAllUrl,
    target: "_self",
  };
});

const sections = computed(() => {
  if (!pageData.value?.properties.grid) return [];
  const sections = pageData.value?.properties.grid.items.map((item) => {
    const { content } = item;
    if (!content) return;
    return getComponentData(content, theme.value);
  });
  const sanitizedSections = sections.filter(Boolean);
  return sanitizedSections;
});
</script>
